import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../ui/button';

const navLinks = [
  { href: '/#inicio', label: 'Inicio' },
  { href: '/#sobre-mi', label: 'Sobre Mí' },
  { href: '/#portfolio', label: 'Portfolio' },
  { href: '/#servicios', label: 'Servicios' },
  { href: '/blog', label: 'Blog' },
  { href: '/#contacto', label: 'Contacto' },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const clientLoggedIn = !!sessionStorage.getItem('client_token');
  const clientLabel = clientLoggedIn ? 'Mi área' : 'Acceder';
  const clientLinkProps = clientLoggedIn
    ? { href: '/private' }
    : { href: '/client/login', target: '_blank', rel: 'noopener noreferrer' };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    if (href.startsWith('/#')) {
      e.preventDefault();
      const id = href.replace('/#', '');
      if (location.pathname !== '/') {
        window.location.href = href;
      } else {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  // Check if we're on the home page (hero section visible)
  const isHomePage = location.pathname === '/';

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
        isScrolled 
          ? 'bg-background/95 backdrop-blur-md border-b border-border shadow-sm' 
          : isHomePage 
            ? ''
            : 'bg-background/95 backdrop-blur-md border-b border-border'
      }`}
      data-testid="navbar"
      style={{ pointerEvents: 'auto' }}
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl relative">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center"
            data-testid="logo"
          >
            {/* Logo claro (para tema claro) */}
            <img 
              src="https://customer-assets.emergentagent.com/job_jon-quintano-staging/artifacts/t69v7woh_jonq_logo.png"
              alt="Jon Quintano Photography"
              className={`h-12 md:h-14 w-auto ${
                !isScrolled && isHomePage ? 'hidden' : 'dark:hidden block'
              }`}
            />
            {/* Logo oscuro (para tema oscuro y hero) */}
            <img 
              src="https://customer-assets.emergentagent.com/job_jon-quintano-staging/artifacts/6hee9rk4_mio_logo_dark.png"
              alt="Jon Quintano Photography"
              className={`h-12 md:h-14 w-auto ${
                !isScrolled && isHomePage ? 'block' : 'dark:block hidden'
              }`}
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className={`text-xs uppercase tracking-widest transition-colors ${
                  !isScrolled && isHomePage 
                    ? 'text-white/90 hover:text-white' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-full transition-colors ${
                !isScrolled && isHomePage 
                  ? 'text-white hover:bg-white/20' 
                  : 'hover:bg-muted'
              }`}
              data-testid="theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>

            <a 
              {...clientLinkProps}
              className="hidden md:block"
            >
              <Button 
                variant="outline" 
                size="sm" 
                className={!isScrolled && isHomePage ? 'border-white/50 text-white hover:bg-white/10' : ''}
                data-testid="login-btn"
              >
                {clientLabel}
              </Button>
            </a>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              onTouchStart={(e) => {
                e.stopPropagation();
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsOpen(prev => !prev);
              }}
              className={`lg:hidden relative z-[110] p-4 -mr-3 touch-manipulation select-none ${!isScrolled && isHomePage ? 'text-white' : ''}`}
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
              aria-expanded={isOpen}
              style={{ 
                WebkitTapHighlightColor: 'transparent',
                WebkitTouchCallout: 'none',
                WebkitUserSelect: 'none',
                touchAction: 'manipulation'
              }}
            >
              {isOpen ? <X className="w-7 h-7 pointer-events-none" /> : <Menu className="w-7 h-7 pointer-events-none" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-background border-b border-border overflow-hidden relative z-[100]"
          >
            <nav className="container mx-auto px-4 py-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault();
                    setIsOpen(false);
                    
                    if (link.href.startsWith('/#')) {
                      const id = link.href.replace('/#', '');
                      if (location.pathname !== '/') {
                        window.location.href = link.href;
                      } else {
                        setTimeout(() => {
                          const el = document.getElementById(id);
                          if (el) el.scrollIntoView({ behavior: 'smooth' });
                        }, 150);
                      }
                    } else {
                      window.location.href = link.href;
                    }
                  }}
                  className="block text-sm uppercase tracking-widest py-4 border-b border-border/50 last:border-0 active:bg-muted/50"
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </a>
              ))}
              <a 
                {...clientLinkProps}
                className="block mt-4"
                onClick={() => setIsOpen(false)}
              >
                <Button variant="outline" className="w-full" data-testid="mobile-login-btn">
                  {clientLabel}
                </Button>
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};
